import React, { useCallback, useEffect, useState } from 'react';
import { Card, Tab } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  CreateUpdateClientGroupDto,
  GetClientGroupDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { ClientGroupModalEditor } from '../../components/ClientGroups/ClientGroupModalEditor';
import { ClientGroupDetails } from '../../components/ClientGroups/ClientGroupDetails';
import { ClientGroupEditorForm } from '../../components/ClientGroups/ClientGroupEditorForm';
import { ClientGroupWithoutContact } from '../../components/ClientGroups/ClientGroupWithoutContact';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import {
  validate,
  convertClientGroup,
} from '../../../common/pokCore/contexts/ClientGroupContext';
import { useNotifications } from '../../../common/hooks/useNotifications';
import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { TabsHistory } from '../../../app/components/TabsHistory';

export const ClientGroupView: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const pok = usePokCore();
  const notifications = useNotifications();
  const [group, setGroup] = useState<GetClientGroupDto>();
  const [showEdit, setShowEdit] = useState(false);
  const { t, tk } = useTranslation('clientLng');

  const loadGroup = useCallback(async () => {
    if (!groupId) {
      return;
    }
    try {
      const data = await pok.clientGroups.getById(groupId);
      setGroup(data);
    } catch (err) {
      notifications.caughtError(err as ResponseError);
    }
  }, [groupId, pok.clientGroups, notifications]);

  useEffect(() => {
    void loadGroup();
  }, [loadGroup]);

  const handleEdit = async (dto: CreateUpdateClientGroupDto, id?: string) => {
    const status = await validate(dto);
    if (status.valid) {
      try {
        await pok.clientGroups.update(id!, dto);
        setShowEdit(false);
        void loadGroup();
        return { saved: true, errors: [] };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return { saved: false, errors: status.errors };
    }
  };

  if (!group) {
    return null;
  }

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          {t(tk.clientGroup.clientGroup)}: <b>{group.name} </b>, nr sys.{' '}
          {group.systemNumber}{' '}
          {group?.active ? '' : ` (${t(tk.common.nonActiveShe)})`}{' '}
        </Card.Title>
        <TabsHistory activeKey="info" id="client-group-tabs">
          <Tab eventKey="info" title={t(tk.common.name)} className="m-3">
            <ClientGroupEditorForm
              clientGroup={convertClientGroup(group)}
              readOnly
            />
            <div className="d-flex justify-content-end pt-3">
              <Button
                variant="outline-primary"
                onClick={() => setShowEdit(true)}
              >
                {t(tk.button.edit)}
              </Button>
            </div>
          </Tab>
          <Tab
            eventKey="clients"
            title={t(tk.clientGroup.groupClients)}
            className="m-3"
          >
            <ClientGroupDetails group={group} onRefresh={loadGroup} />
          </Tab>
          {process.env.REACT_APP_SURVEYS_CLIENTS?.toLocaleLowerCase() ===
            'on' && (
            <Tab
              eventKey="groupWithoutContact"
              title={t(tk.clientGroup.groupWithoutContactTab)}
              className="m-3"
            >
              <ClientGroupWithoutContact groupId={group.id} />
            </Tab>
          )}
        </TabsHistory>

        <ClientGroupModalEditor
          id={group.id}
          systemNumber={group.systemNumber}
          clientGroup={convertClientGroup(group)}
          onCompleted={handleEdit}
          show={showEdit}
          handleClose={() => setShowEdit(false)}
        />
      </Card.Body>
    </Card>
  );
};
